import {
  AssignPermissionDto,
  BulkAssignPermissionsDto,
  BulkAssignResponse,
  GroupedRolePermissions,
  RoleWithPermissions,
} from '../models/role.model';
import { apiService } from './api.service';

/**
 * Role Permission Service
 * Handles all role-permission relationship API calls
 */
class RolePermissionService {
  private baseUrl = '/roles';

  /**
   * Get all permissions for a specific role (ADMIN — inspects any role,
   * backend-gated by `configuration.roles_permissions.view_roles`).
   */
  async getRolePermissions(roleId: string): Promise<RoleWithPermissions> {
    return await apiService.get<RoleWithPermissions>(
      `${this.baseUrl}/${roleId}/permissions`
    );
  }

  /**
   * Get the CURRENT user's OWN permissions — self-scoped, NOT gated by
   * an admin permission. `useUserPermissions` reads this so a non-admin
   * role (cashier / vendor) can resolve its own `usePermissionCheck`
   * gates. Using the admin `getRolePermissions(roleId)` here 403'd every
   * non-admin and emptied their perm list → "Acceso restringido" on
   * every page.
   */
  async getMyPermissions(): Promise<RoleWithPermissions> {
    return await apiService.get<RoleWithPermissions>(
      `${this.baseUrl}/me/permissions`
    );
  }

  /**
   * Get permissions grouped by category for a role
   */
  async getRolePermissionsByCategory(
    roleId: string
  ): Promise<GroupedRolePermissions[]> {
    return await apiService.get<GroupedRolePermissions[]>(
      `${this.baseUrl}/${roleId}/permissions/grouped`
    );
  }

  /**
   * Assign a single permission to a role
   */
  async assignPermission(
    roleId: string,
    data: AssignPermissionDto
  ): Promise<void> {
    await apiService.post<void>(
      `${this.baseUrl}/${roleId}/permissions`,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Bulk assign permissions to a role
   */
  async bulkAssignPermissions(
    roleId: string,
    data: BulkAssignPermissionsDto
  ): Promise<BulkAssignResponse> {
    return await apiService.post<BulkAssignResponse>(
      `${this.baseUrl}/${roleId}/permissions/bulk`,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Remove a permission from a role
   */
  async removePermission(roleId: string, permissionId: string): Promise<void> {
    await apiService.delete(
      `${this.baseUrl}/${roleId}/permissions/${permissionId}`
    );
  }
}

export const rolePermissionService = new RolePermissionService();
